import { useState, useEffect, useRef, useCallback } from "react";

// ─── Simulation Engine ────────────────────────────────────────────────────────

const SAMPLING_RATE = 1000;
const WINDOW_SEC = 60;
const SMALL_WINDOW_SEC = 10;

// Generate a realistic flow record
function makeFlow(t, { isDdos = false, samplingRate = SAMPLING_RATE } = {}) {
  const isShort = Math.random() < 0.7; // 70% of flows are short
  const durationMs = isShort
    ? Math.floor(Math.random() * 800) + 10   // 10–810ms
    : Math.floor(Math.random() * 50000) + 1000; // 1–51s

  const pktSize = isDdos
    ? Math.floor(Math.random() * 80) + 40    // small UDP flood packets
    : Math.floor(Math.random() * 1300) + 200;

  // With sampling, most short flows only get 1 packet observed
  const sampledPkts = isShort ? 1 : Math.max(1, Math.floor(durationMs / 1000 / (samplingRate / 10)));
  const sampledBytes = sampledPkts * pktSize;

  const flowStart = t - durationMs;
  const flowEnd = t;

  return {
    time_received: t,
    flow_start: flowStart,
    flow_end: flowEnd,
    bytes: sampledBytes,
    packets: sampledPkts,
    sampling_rate: samplingRate,
    is_zero_dur: flowEnd === flowStart || durationMs < 1000,
  };
}

// Generate a batch of flows "received" in a given ms-tick range
function generateFlows(fromMs, toMs, rate = 30, isDdos = false) {
  const count = isDdos ? rate * 6 : rate;
  return Array.from({ length: count }, () =>
    makeFlow(fromMs + Math.random() * (toMs - fromMs), { isDdos })
  );
}

// ─── Calculation Methods ──────────────────────────────────────────────────────

// Method 1: Original – fixed 60s window divisor
function method_fixed60(flows, windowSec = 60) {
  const totalBytes = flows.reduce((s, f) => s + f.bytes * f.sampling_rate, 0);
  const totalPkts = flows.reduce((s, f) => s + f.packets * f.sampling_rate, 0);
  return { bps: (totalBytes * 8) / windowSec, pps: totalPkts / windowSec };
}

// Method 2: Per-flow duration (clamp to 1s min)
function method_perflow(flows) {
  let bps = 0, pps = 0;
  for (const f of flows) {
    const dur = Math.max((f.flow_end - f.flow_start) / 1000, 1);
    bps += (f.bytes * f.sampling_rate * 8) / dur;
    pps += (f.packets * f.sampling_rate) / dur;
  }
  return { bps, pps };
}

// Method 3: Small window (10s) by time_received
function method_smallwindow(flows, windowSec = 10) {
  const totalBytes = flows.reduce((s, f) => s + f.bytes * f.sampling_rate, 0);
  const totalPkts = flows.reduce((s, f) => s + f.packets * f.sampling_rate, 0);
  return { bps: (totalBytes * 8) / windowSec, pps: totalPkts / windowSec };
}

// Method 4: Aggregate window (correct for sampled data)
function method_aggregate(flows, windowSec = 10) {
  return method_smallwindow(flows, windowSec); // same math, different framing
}

// ─── Formatting Helpers ───────────────────────────────────────────────────────
function fmtBps(bps) {
  if (bps >= 1e9) return `${(bps / 1e9).toFixed(2)} Gbps`;
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(2)} Mbps`;
  if (bps >= 1e3) return `${(bps / 1e3).toFixed(2)} Kbps`;
  return `${bps.toFixed(0)} bps`;
}
function fmtPps(pps) {
  if (pps >= 1e6) return `${(pps / 1e6).toFixed(2)} Mpps`;
  if (pps >= 1e3) return `${(pps / 1e3).toFixed(2)} Kpps`;
  return `${pps.toFixed(0)} pps`;
}

// ─── Mini Sparkline ───────────────────────────────────────────────────────────
function Sparkline({ data, color, height = 40 }) {
  if (!data || data.length < 2) return <div style={{ height }} />;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 200;
    const y = height - (v / max) * (height - 4);
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 200 ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <polygon points={`0,${height} ${pts} 200,${height}`} fill={color} opacity="0.12" />
    </svg>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [tab, setTab] = useState(0); // 0=Learn, 1=Demo
  const [running, setRunning] = useState(false);
  const [isDdos, setIsDdos] = useState(false);
  const [tick, setTick] = useState(0);
  const [allFlows, setAllFlows] = useState([]);
  const [history, setHistory] = useState({ m1: [], m2: [], m3: [], m4: [] });
  const [zeroDurPct, setZeroDurPct] = useState(0);
  const [ddosStart, setDdosStart] = useState(null);
  const nowRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const isDdosRef = useRef(isDdos);
  isDdosRef.current = isDdos;

  const step = useCallback(() => {
    const now = nowRef.current + 1000;
    nowRef.current = now;
    const newFlows = generateFlows(now - 1000, now, 30, isDdosRef.current);

    setAllFlows(prev => {
      const merged = [...prev, ...newFlows];
      // Keep last 120 seconds of flows
      const cutoff = now - 120000;
      return merged.filter(f => f.time_received >= cutoff);
    });

    setTick(t => t + 1);
  }, []);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(step, 500); // 2x speed for demo
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running, step]);

  // Recompute results whenever flows or tick changes
  useEffect(() => {
    const now = nowRef.current;

    // Each method uses flows received in its own window
    const flows60 = allFlows.filter(f => f.time_received >= now - WINDOW_SEC * 1000);
    const flows10 = allFlows.filter(f => f.time_received >= now - SMALL_WINDOW_SEC * 1000);

    const r1 = method_fixed60(flows60, 60);
    const r2 = method_perflow(flows60);
    const r3 = method_smallwindow(flows10, 10);
    const r4 = method_aggregate(flows10, 10);

    const zd = flows60.length > 0
      ? (flows60.filter(f => f.is_zero_dur).length / flows60.length) * 100
      : 0;
    setZeroDurPct(zd);

    setHistory(h => ({
      m1: [...h.m1.slice(-59), r1.bps],
      m2: [...h.m2.slice(-59), r2.bps],
      m3: [...h.m3.slice(-59), r3.bps],
      m4: [...h.m4.slice(-59), r4.bps],
    }));
  }, [tick, allFlows]);

  const handleDdosToggle = () => {
    setIsDdos(d => {
      if (!d) setDdosStart(nowRef.current);
      else setDdosStart(null);
      return !d;
    });
  };

  const handleReset = () => {
    setRunning(false);
    setIsDdos(false);
    setDdosStart(null);
    setAllFlows([]);
    setHistory({ m1: [], m2: [], m3: [], m4: [] });
    setTick(0);
    nowRef.current = Date.now();
  };

  const currentResults = (() => {
    const now = nowRef.current;
    const flows60 = allFlows.filter(f => f.time_received >= now - WINDOW_SEC * 1000);
    const flows10 = allFlows.filter(f => f.time_received >= now - SMALL_WINDOW_SEC * 1000);
    return {
      m1: method_fixed60(flows60, 60),
      m2: method_perflow(flows60),
      m3: method_smallwindow(flows10, 10),
      m4: method_aggregate(flows10, 10),
    };
  })();

  const methods = [
    {
      id: "m1",
      label: "Fixed 60s Window",
      tag: "Original",
      tagColor: "#f59e0b",
      color: "#f59e0b",
      bps: currentResults.m1.bps,
      pps: currentResults.m1.pps,
      hist: history.m1,
      sql: `SELECT\n  startOfMinute(time_received) AS time,\n  sum(bytes * sampling_rate * 8) / 60 AS bps,\n  sum(packets * sampling_rate) / 60 AS pps\nFROM flows\nGROUP BY time`,
      pros: "Simple. Consistent 1-minute granularity.",
      cons: "Introduces up to 60s of detection lag. Misses attack onset completely until the full window closes.",
      problem: "lag",
    },
    {
      id: "m2",
      label: "Per-Flow Duration",
      tag: "Improved",
      tagColor: "#60a5fa",
      color: "#60a5fa",
      bps: currentResults.m2.bps,
      pps: currentResults.m2.pps,
      hist: history.m2,
      sql: `SELECT\n  startOfMinute(time_received) AS time,\n  sum(bytes * sampling_rate * 8 /\n    greatest(dateDiff('second',\n      flow_start, flow_end), 1)\n  ) AS bps\nFROM flows\nGROUP BY time`,
      pros: "More accurate for long-lived flows with known duration.",
      cons: "With high sampling, most flows appear as 1-packet (zero duration). Clamping to 1s causes massive underestimation — can be 100x off.",
      problem: "sampling",
    },
    {
      id: "m3",
      label: "Small Window (10s)",
      tag: "Faster",
      tagColor: "#a78bfa",
      color: "#a78bfa",
      bps: currentResults.m3.bps,
      pps: currentResults.m3.pps,
      hist: history.m3,
      sql: `SELECT\n  toStartOfInterval(time_received,\n    INTERVAL 10 SECOND) AS time,\n  sum(bytes * sampling_rate * 8) / 10 AS bps,\n  sum(packets * sampling_rate) / 10 AS pps\nFROM flows\nGROUP BY time`,
      pros: "Faster reaction. Correct divisor matches window size.",
      cons: "Noisier at low traffic. Still depends on flow export timing — router active timeout can still delay records.",
      problem: "noise",
    },
    {
      id: "m4",
      label: "Rolling Aggregate",
      tag: "Best for DDoS",
      tagColor: "#34d399",
      color: "#34d399",
      bps: currentResults.m4.bps,
      pps: currentResults.m4.pps,
      hist: history.m4,
      sql: `-- Run every 10s, query last 60s of RECEIVED flows\nSELECT\n  src_ip,\n  sum(bytes * sampling_rate * 8) / 60 AS bps,\n  sum(packets * sampling_rate)    / 60 AS pps\nFROM flows\nWHERE time_received >= now() - INTERVAL 60 SECOND\nGROUP BY src_ip\nHAVING bps > 1e9`,
      pros: "Uses time_received as anchor. Re-queries as data arrives. Doesn't need flow duration. Statistically valid across aggregates. Best for sampled data.",
      cons: "Flow arrival timing adds ~5–15s smearing. Precision is lower than per-flow, but attack signal is strong enough to show through.",
      problem: null,
    },
  ];

  return (
    <div style={{
      fontFamily: "'IBM Plex Mono', 'Fira Code', monospace",
      background: "#0a0c10",
      color: "#c9d1d9",
      minHeight: "100vh",
      padding: "0",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=IBM+Plex+Sans:wght@300;400;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; background: #111; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 3px; }
        .tab-btn { background: none; border: none; color: #666; cursor: pointer; font-family: inherit; font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; padding: 10px 20px; transition: color 0.2s; }
        .tab-btn.active { color: #e6edf3; border-bottom: 2px solid #58a6ff; }
        .tab-btn:hover { color: #aaa; }
        .method-card { background: #0d1117; border: 1px solid #21262d; border-radius: 8px; padding: 20px; transition: border-color 0.2s; }
        .method-card:hover { border-color: #30363d; }
        .code-block { background: #161b22; border: 1px solid #21262d; border-radius: 6px; padding: 14px 16px; font-size: 12px; line-height: 1.7; overflow-x: auto; color: #79c0ff; white-space: pre; }
        .btn { border: none; cursor: pointer; font-family: 'IBM Plex Mono', monospace; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; border-radius: 4px; padding: 8px 16px; transition: all 0.15s; }
        .btn-primary { background: #238636; color: #fff; }
        .btn-primary:hover { background: #2ea043; }
        .btn-stop { background: #da3633; color: #fff; }
        .btn-stop:hover { background: #f85149; }
        .btn-ddos { background: #21262d; color: #f85149; border: 1px solid #f85149; }
        .btn-ddos.active { background: #f85149; color: #fff; }
        .btn-ghost { background: #21262d; color: #8b949e; }
        .btn-ghost:hover { background: #30363d; }
        .metric-val { font-size: 22px; font-weight: 600; letter-spacing: -0.02em; }
        .tag { font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; padding: 2px 8px; border-radius: 20px; font-weight: 600; }
        .info-row { display: flex; gap: 8px; margin-top: 10px; }
        .info-pill { font-size: 11px; padding: 4px 10px; border-radius: 4px; flex: 1; }
        .info-pro { background: #0f2a1f; border: 1px solid #238636; color: #3fb950; }
        .info-con { background: #2a1010; border: 1px solid #da3633; color: #f85149; }
        .section-label { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #484f58; margin-bottom: 8px; }
        .ddos-badge { display: inline-block; background: #f85149; color: #fff; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; padding: 2px 8px; border-radius: 3px; animation: pulse 1s ease-in-out infinite alternate; }
        @keyframes pulse { from { opacity: 1; } to { opacity: 0.5; } }
        .learn-section { border-left: 3px solid; padding-left: 20px; margin-bottom: 24px; }
        .step-num { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; font-size: 11px; font-weight: 700; margin-right: 10px; flex-shrink: 0; }
        .formula-box { background: #161b22; border: 1px solid #30363d; border-radius: 6px; padding: 12px 16px; font-size: 13px; color: #e6edf3; margin: 8px 0; }
        .warn-box { background: #2a1810; border: 1px solid #e3b341; border-radius: 6px; padding: 12px 16px; font-size: 12px; color: #e3b341; margin: 8px 0; }
        .good-box { background: #0f2a1f; border: 1px solid #238636; border-radius: 6px; padding: 12px 16px; font-size: 12px; color: #3fb950; margin: 8px 0; }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 700px) { .grid-2 { grid-template-columns: 1fr; } }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: "1px solid #21262d", padding: "16px 28px", display: "flex", alignItems: "center", gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.15em", color: "#484f58", textTransform: "uppercase" }}>IPFIX / NetFlow</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#e6edf3", fontFamily: "'IBM Plex Sans', sans-serif" }}>Traffic Rate Calculation Methods</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {isDdos && <span className="ddos-badge">⚠ DDoS ACTIVE</span>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid #21262d", padding: "0 28px", display: "flex" }}>
        <button className={`tab-btn ${tab === 0 ? "active" : ""}`} onClick={() => setTab(0)}>Learn</button>
        <button className={`tab-btn ${tab === 1 ? "active" : ""}`} onClick={() => setTab(1)}>Live Demo</button>
      </div>

      {/* ── LEARN TAB ── */}
      {tab === 0 && (
        <div style={{ padding: "28px", maxWidth: 860, margin: "0 auto" }}>

          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: "#8b949e", marginBottom: 28, lineHeight: 1.6 }}>
            IPFIX/NetFlow records don't give you instantaneous speed — they give you <em style={{ color: "#c9d1d9" }}>totals over a window</em>. The choice of how you compute rate from those totals has a dramatic impact on detection latency and accuracy, especially during DDoS attacks.
          </div>

          {/* Method 1 */}
          <div className="learn-section" style={{ borderColor: "#f59e0b" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
              <span className="step-num" style={{ background: "#2a1f00", color: "#f59e0b" }}>1</span>
              <span style={{ fontFamily: "'IBM Plex Sans'", fontWeight: 600, fontSize: 16, color: "#f59e0b" }}>Fixed 60s Window</span>
              <span className="tag" style={{ background: "#2a1f00", color: "#f59e0b", marginLeft: 10 }}>Original</span>
            </div>
            <p style={{ fontFamily: "'IBM Plex Sans'", fontSize: 13, color: "#8b949e", lineHeight: 1.7, marginBottom: 10 }}>
              Sum up all bytes/packets received in the last 60 seconds, then divide by 60. Simple, but the fixed divisor means you're always measuring a full minute — you can't see anything shorter than that.
            </p>
            <div className="formula-box">
              bps = sum(bytes × sampling_rate × 8) <span style={{ color: "#f59e0b" }}>/ 60</span>
            </div>
            <div className="warn-box">
              ⚠ If an attack starts at second 5 of a minute, your query won't show the true rate until second 60. You've introduced up to <strong>55 seconds of artificial lag</strong>.
            </div>
          </div>

          {/* Method 2 */}
          <div className="learn-section" style={{ borderColor: "#60a5fa" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
              <span className="step-num" style={{ background: "#001a2a", color: "#60a5fa" }}>2</span>
              <span style={{ fontFamily: "'IBM Plex Sans'", fontWeight: 600, fontSize: 16, color: "#60a5fa" }}>Per-Flow Duration</span>
              <span className="tag" style={{ background: "#001a2a", color: "#60a5fa", marginLeft: 10 }}>Improved</span>
            </div>
            <p style={{ fontFamily: "'IBM Plex Sans'", fontSize: 13, color: "#8b949e", lineHeight: 1.7, marginBottom: 10 }}>
              Instead of a fixed window, divide each flow's bytes by its own measured duration. Clamp to 1s minimum to avoid division by zero.
            </p>
            <div className="formula-box">
              duration = max(flow_end − flow_start, <span style={{ color: "#60a5fa" }}>1s</span>)<br />
              bps = sum(bytes × sampling_rate × 8 / duration)
            </div>
            <div className="warn-box">
              ⚠ <strong>The sampling trap:</strong> With 1:1000 sampling, a short flow that had 10 packets total will likely be observed with just <strong>1 sampled packet</strong>. One packet means flow_start == flow_end, so duration = 0ms → clamped to 1s. But the real flow lasted 10ms, so you're off by <strong>100×</strong>.
            </div>
            <div className="formula-box" style={{ fontSize: 12 }}>
              Real:    bps = (1500B × 8) / 0.010s = <span style={{ color: "#f85149" }}>1.2 Gbps</span><br />
              Sampled: bps = (1500 × 1000 × 8) / <span style={{ color: "#60a5fa" }}>1s</span>  = <span style={{ color: "#3fb950" }}>12 Mbps</span>  ← 100× underestimate
            </div>
          </div>

          {/* Method 3 */}
          <div className="learn-section" style={{ borderColor: "#a78bfa" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
              <span className="step-num" style={{ background: "#1a0a2a", color: "#a78bfa" }}>3</span>
              <span style={{ fontFamily: "'IBM Plex Sans'", fontWeight: 600, fontSize: 16, color: "#a78bfa" }}>Small Window (10s)</span>
              <span className="tag" style={{ background: "#1a0a2a", color: "#a78bfa", marginLeft: 10 }}>Faster</span>
            </div>
            <p style={{ fontFamily: "'IBM Plex Sans'", fontSize: 13, color: "#8b949e", lineHeight: 1.7, marginBottom: 10 }}>
              Shrink the aggregation window from 60s to 10s and update the divisor to match. You get a new data point every 10 seconds instead of every 60.
            </p>
            <div className="formula-box">
              bps = sum(bytes × sampling_rate × 8) / <span style={{ color: "#a78bfa" }}>10</span>
            </div>
            <div className="warn-box">
              ⚠ Smaller windows are noisier — a bursty but legitimate transfer looks alarming in 5s but normal in 60s. Also, if your router's active timeout is 60s, flow records still won't arrive until the router decides to send them. The query window and exporter timeout need to be in sync.
            </div>
          </div>

          {/* Method 4 */}
          <div className="learn-section" style={{ borderColor: "#34d399" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
              <span className="step-num" style={{ background: "#0a2a1a", color: "#34d399" }}>4</span>
              <span style={{ fontFamily: "'IBM Plex Sans'", fontWeight: 600, fontSize: 16, color: "#34d399" }}>Rolling Aggregate Window</span>
              <span className="tag" style={{ background: "#0a2a1a", color: "#34d399", marginLeft: 10 }}>Best for DDoS</span>
            </div>
            <p style={{ fontFamily: "'IBM Plex Sans'", fontSize: 13, color: "#8b949e", lineHeight: 1.7, marginBottom: 10 }}>
              The key insight: <strong style={{ color: "#c9d1d9" }}>stop computing per-flow rates entirely.</strong> For sampled data, sampling destroys duration information for short flows irreversibly. Instead, compute aggregate rate over a rolling window anchored to <code style={{ color: "#79c0ff" }}>time_received</code> — when the record arrived at your collector — and re-run the query every few seconds.
            </p>
            <div className="formula-box">
              <span style={{ color: "#484f58" }}>-- Run every 10s. Window of received flows:</span><br />
              bps = sum(bytes × sampling_rate × 8) / <span style={{ color: "#34d399" }}>window_sec</span><br />
              <span style={{ color: "#484f58" }}>-- No per-flow duration math needed at all.</span>
            </div>
            <div className="good-box">
              ✓ <strong>Why this works:</strong> You're not asking "what is the rate of this flow?" — you're asking "how many bytes arrived at my collector in the last N seconds, and what does that imply about total traffic?" The law of large numbers makes this statistically valid across the aggregate, even when individual flow durations are unknown or corrupted by sampling.
            </div>
            <div className="warn-box">
              ⚠ <strong>One honest limitation:</strong> Flow records arrive with variable delay (active/idle timeout smearing). <code>time_received</code> is a reliable anchor, but some flows represent traffic from 30–60s ago. For DDoS detection this is fine — the attack signal is orders of magnitude above baseline and shows through the noise.
            </div>
          </div>

          {/* Summary table */}
          <div style={{ marginTop: 8 }}>
            <div className="section-label">Summary Comparison</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #21262d" }}>
                    {["Method", "Detection Lag", "Sampling Safe?", "Noise Level", "Best For"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", color: "#484f58", fontWeight: 600, textAlign: "left", letterSpacing: "0.08em", fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Fixed 60s", "Up to 60s", "✓ Yes", "Low", "Capacity planning"],
                    ["Per-flow dur.", "Up to 60s", "✗ No — 100× error", "Medium", "Unsampled flows only"],
                    ["Small window", "~10s", "✓ Yes", "High", "Low sampling rates"],
                    ["Rolling aggregate", "~10–15s", "✓ Yes", "Medium", "DDoS detection ✓"],
                  ].map(([m, lag, safe, noise, best], i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #161b22" }}>
                      <td style={{ padding: "8px 12px", color: ["#f59e0b","#60a5fa","#a78bfa","#34d399"][i], fontWeight: 600 }}>{m}</td>
                      <td style={{ padding: "8px 12px", color: i === 3 ? "#3fb950" : "#f85149" }}>{lag}</td>
                      <td style={{ padding: "8px 12px", color: safe.startsWith("✗") ? "#f85149" : "#3fb950" }}>{safe}</td>
                      <td style={{ padding: "8px 12px", color: "#8b949e" }}>{noise}</td>
                      <td style={{ padding: "8px 12px", color: "#8b949e" }}>{best}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── DEMO TAB ── */}
      {tab === 1 && (
        <div style={{ padding: "20px 28px" }}>

          {/* Controls */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 20, flexWrap: "wrap" }}>
            {!running
              ? <button className="btn btn-primary" onClick={() => setRunning(true)}>▶ Start Simulation</button>
              : <button className="btn btn-stop" onClick={() => setRunning(false)}>■ Pause</button>
            }
            <button
              className={`btn btn-ddos ${isDdos ? "active" : ""}`}
              onClick={handleDdosToggle}
              disabled={!running}
            >
              {isDdos ? "✕ Stop DDoS" : "⚡ Trigger DDoS"}
            </button>
            <button className="btn btn-ghost" onClick={handleReset}>↺ Reset</button>
            <div style={{ marginLeft: "auto", fontSize: 11, color: "#484f58" }}>
              {allFlows.length} flow records in memory · {zeroDurPct.toFixed(0)}% zero-duration
              {zeroDurPct > 40 && <span style={{ color: "#f85149", marginLeft: 8 }}>⚠ High zero-dur — per-flow method unreliable</span>}
            </div>
          </div>

          {!running && allFlows.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#484f58", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>▶</div>
              <div>Start the simulation to see all four methods in real time.</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>Then trigger a DDoS to see how quickly each method reacts.</div>
            </div>
          )}

          <div className="grid-2">
            {methods.map((m) => (
              <div key={m.id} className="method-card" style={{ borderColor: allFlows.length > 0 ? `${m.color}33` : "#21262d" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: m.color, display: "inline-block", flexShrink: 0 }} />
                  <span style={{ fontFamily: "'IBM Plex Sans'", fontWeight: 600, fontSize: 14, color: "#e6edf3" }}>{m.label}</span>
                  <span className="tag" style={{ background: `${m.color}22`, color: m.tagColor, marginLeft: "auto" }}>{m.tag}</span>
                </div>

                {/* Metrics */}
                <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
                  <div>
                    <div className="section-label">Throughput</div>
                    <div className="metric-val" style={{ color: m.color }}>{fmtBps(m.bps)}</div>
                  </div>
                  <div>
                    <div className="section-label">Packet Rate</div>
                    <div className="metric-val" style={{ color: m.color }}>{fmtPps(m.pps)}</div>
                  </div>
                </div>

                {/* Sparkline */}
                <div style={{ marginBottom: 12 }}>
                  <Sparkline data={m.hist} color={m.color} height={44} />
                </div>

                {/* SQL */}
                <div className="section-label" style={{ marginTop: 8 }}>Query Pattern</div>
                <pre className="code-block">{m.sql}</pre>

                {/* Pro/Con */}
                <div className="info-row">
                  <div className="info-pill info-pro">✓ {m.pros}</div>
                </div>
                <div className="info-row">
                  <div className="info-pill info-con">✗ {m.cons}</div>
                </div>
              </div>
            ))}
          </div>

          {isDdos && (
            <div style={{ marginTop: 20, background: "#2a0a0a", border: "1px solid #f85149", borderRadius: 8, padding: 16, fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13 }}>
              <span style={{ color: "#f85149", fontWeight: 700 }}>DDoS ACTIVE</span>
              <span style={{ color: "#8b949e", marginLeft: 12 }}>
                Observe how <span style={{ color: "#f59e0b" }}>Method 1</span> reacts slowly due to the 60s divisor smoothing the spike.
                <span style={{ color: "#34d399", marginLeft: 6 }}>Method 4</span> reacts fastest because it sees incoming records as they arrive, without waiting for the window to close.
                <span style={{ color: "#60a5fa", marginLeft: 6 }}>Method 2</span> underestimates due to zero-duration flow clamping with high sampling.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
